import React, { useEffect, useRef, useCallback } from "react";

import { Box, useTheme, IconButton, Divider, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatStrikethroughIcon from "@mui/icons-material/FormatStrikethrough";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TextStyle from "@tiptap/extension-text-style";
import { Extension } from "@tiptap/core";

import NativePuckTitleField from "./NativePuckTitleField";

const PUCK_CONFIG = {
    FontFamily: "var(--puck-font-family) !important",
};

const FontSize = Extension.create({
    name: "fontSize",
    addOptions() {
        return {
            types: ["textStyle"],
        };
    },
    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    fontSize: {
                        default: "medium",
                        parseHTML: (element) => element.style.fontSize?.replace(/["']/g, ""),
                        renderHTML: (attributes) => {
                            if (!attributes.fontSize) return {};
                            return {
                                style: `font-size: ${attributes.fontSize}`,
                            };
                        },
                    },
                },
            },
        ];
    },
});

const menuBarSx = (theme: Theme) => ({
    padding: theme.spacing(0.5),
    border: `1px solid ${theme.palette.divider}`,
    borderBottom: "none",
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
    display: "flex",
    justifyContent: "flex-start",
    flexWrap: "wrap",
    gap: theme.spacing(0),
    backgroundColor: theme.palette.background.paper,
});

const menuButtonSx = (theme: Theme, isActive?: boolean) => ({
    background: isActive ? `${theme.palette.primary[50]}` : "transparent",
    border: "none",
    borderRadius: theme.spacing(0.5),
    fontFamily: PUCK_CONFIG.FontFamily,
    height: theme.spacing(3),
    width: theme.spacing(3),
    margin: 0,
    padding: 0,
    minWidth: "auto",
    color: isActive ? theme.palette.primary.main : "inherit",
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
    },
    "&.Mui-focusVisible": {
        outline: `2px solid ${theme.palette.primary.light}`,
    },
    "& .MuiTypography-root": {
        color: isActive ? theme.palette.primary.main : "inherit",
    },
});

const editorContainerSx = (theme: Theme) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "0 0 4px 4px",
    "& .ProseMirror": {
        minHeight: 150,
        maxHeight: 400,
        overflowY: "auto",
        padding: theme.spacing(1.5),
        outline: "none",
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.fontSize,
        "& p": {
            margin: `${theme.spacing(0.5)} 0`,
        },
        "&:focus": {
            outline: "none",
        },
        "& ul, & ol": {
            paddingLeft: `${theme.spacing(1.5)}`,
        },
        "& a": {
            color: theme.palette.primary.main,
            textDecoration: "underline",
        },
        "& img.editor-image": {
            maxWidth: "100%",
            height: "auto",
            display: "block",
            borderRadius: theme.shape.borderRadius,
            margin: "1em auto",
            boxShadow: theme.shadows[1],
        },
        '& [style*="font-size: small"]': {
            fontSize: `${theme.typography.fontSize * 0.75}px`,
        },
        '& [style*="font-size: medium"]': {
            fontSize: `${theme.typography.fontSize}px`,
        },
        '& [style*="font-size: large"]': {
            fontSize: `${theme.typography.fontSize * 1.5}px`,
        },
        "&.ProseMirror-focused": {
            boxShadow: `0 0 0 2px ${theme.palette.primary.light}1a`,
        },
    },
});

export type RichTextEditorFieldProps = {
    onChange: (value: string) => void;
    value?: string;
    label?: string;
};

export const RichTextEditorField = (props: RichTextEditorFieldProps) => {
    const { onChange, value = "" } = props;
    const theme = useTheme();
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    const debouncedOnChange = useCallback(
        (html: string) => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            debounceTimerRef.current = setTimeout(() => {
                onChange(html);
            }, 300);
        },
        [onChange],
    );

    const editor = useEditor({
        extensions: [
            StarterKit,
            Link.configure({
                openOnClick: false,
                autolink: true,
                HTMLAttributes: {
                    rel: "noopener noreferrer",
                    target: "_blank",
                },
            }),
            Placeholder.configure({
                placeholder: "Write something...",
            }),
            TextStyle,
            FontSize.configure({
                default: "medium",
                types: ["textStyle"],
            }),
        ],
        content: value,
        autofocus: "end",
        editorProps: {
            attributes: {
                class: "focus-visible",
            },
        },
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            debouncedOnChange(html);
        },
        onBlur: ({ editor }) => {
            const html = editor.getHTML();
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            onChange(html);
        },
    });

    useEffect(() => {
        if (editor && editor.getHTML() !== value) {
            editor.commands.setContent(value);
        }
    }, [value, editor]);

    const MenuItems = () => {
        if (!editor) return null;

        const handleBold = (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            editor.chain().focus().toggleBold().run();
        };

        const handleItalic = (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            editor.chain().focus().toggleItalic().run();
        };

        const handleStrike = (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            editor.chain().focus().toggleStrike().run();
        };

        const handleBulletList = (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            editor.chain().focus().toggleBulletList().run();
        };

        const handleOrderedList = (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            editor.chain().focus().toggleOrderedList().run();
        };

        const handleSmallText = (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            editor.chain().focus().setMark("textStyle", { fontSize: "small" }).run();
        };

        const handleMediumText = (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            editor.chain().focus().setMark("textStyle", { fontSize: "medium" }).run();
        };

        const handleLargeText = (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            editor.chain().focus().setMark("textStyle", { fontSize: "large" }).run();
        };

        return (
            <Box sx={(theme) => menuBarSx(theme)}>
                <IconButton
                    onClick={handleBold}
                    onMouseDown={(e) => e.preventDefault()}
                    title="Bold"
                    size="small"
                    sx={(theme) => menuButtonSx(theme, editor.isActive("bold"))}
                >
                    <FormatBoldIcon fontSize="small" />
                </IconButton>
                <IconButton
                    onClick={handleItalic}
                    onMouseDown={(e) => e.preventDefault()}
                    title="Italic"
                    size="small"
                    sx={(theme) => menuButtonSx(theme, editor.isActive("italic"))}
                >
                    <FormatItalicIcon fontSize="small" />
                </IconButton>
                <IconButton
                    onClick={handleStrike}
                    onMouseDown={(e) => e.preventDefault()}
                    title="Strike"
                    size="small"
                    sx={(theme) => menuButtonSx(theme, editor.isActive("strike"))}
                >
                    <FormatStrikethroughIcon fontSize="small" />
                </IconButton>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <IconButton
                    onClick={handleSmallText}
                    onMouseDown={(e) => e.preventDefault()}
                    title="Small text"
                    size="small"
                    sx={(theme) => ({
                        ...menuButtonSx(theme, editor.isActive("textStyle", { fontSize: "small" })),
                        fontSize: theme.typography.h6,
                        fontFamily: theme.typography.fontFamily,
                        fontWeight: "bold",
                    })}
                >
                    <Typography variant="h6">{"S"}</Typography>
                </IconButton>
                <IconButton
                    onClick={handleMediumText}
                    onMouseDown={(e) => e.preventDefault()}
                    title="Normal text"
                    size="small"
                    sx={(theme) => ({
                        ...menuButtonSx(theme, editor.isActive("textStyle", { fontSize: "medium" })),
                        fontSize: theme.typography.h4,
                        fontFamily: theme.typography.fontFamily,
                        fontWeight: "bold",
                    })}
                >
                    <Typography variant="h4">{"M"}</Typography>
                </IconButton>
                <IconButton
                    onClick={handleLargeText}
                    onMouseDown={(e) => e.preventDefault()}
                    title="Large text"
                    size="small"
                    sx={(theme) => ({
                        ...menuButtonSx(theme, editor.isActive("textStyle", { fontSize: "large" })),
                        fontSize: theme.typography.h3,
                        fontFamily: theme.typography.fontFamily,
                        fontWeight: "bold",
                    })}
                >
                    <Typography variant="h3">{"L"}</Typography>
                </IconButton>

                <Divider orientation="vertical" flexItem sx={{ mx: theme.spacing(0.5) }} />

                <IconButton
                    onClick={handleBulletList}
                    onMouseDown={(e) => e.preventDefault()}
                    title="Bullet List"
                    size="small"
                    sx={(theme) => menuButtonSx(theme, editor.isActive("bulletList"))}
                >
                    <FormatListBulletedIcon fontSize="small" />
                </IconButton>
                <IconButton
                    onClick={handleOrderedList}
                    onMouseDown={(e) => e.preventDefault()}
                    title="Numbered List"
                    size="small"
                    sx={(theme) => menuButtonSx(theme, editor.isActive("orderedList"))}
                >
                    <FormatListNumberedIcon fontSize="small" />
                </IconButton>
            </Box>
        );
    };

    return (
        <Box>
            <Box sx={{ mb: theme.spacing(1), pl: theme.spacing(0.5) }}>
                <NativePuckTitleField label="Content" icon="edit" />
            </Box>

            <Box>
                <MenuItems />
                <Box sx={(theme) => editorContainerSx(theme)}>
                    <EditorContent editor={editor} />
                </Box>
            </Box>
        </Box>
    );
};

export default RichTextEditorField;
